import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize, Subscription } from 'rxjs';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  Factory,
  FactoryBatchStock,
  FactoryStockItem,
  ProductTransfer,
  ProductTransferDeleteError,
  ProductTransferItemError,
  ProductTransferRequest,
} from '../../production-inventory.model';
import { ProductionInventoryService } from '../../services/production-inventory.service';
import { PaginatedResponse } from '../../../common/models/common.model';
import { RawMaterialService } from '../../../raw-material-inventory/raw-material.service';
import { RawMaterialWarehouse } from '../../../raw-material-inventory/raw-material.model';
import { DealersService } from '../../../dealers/dealers.service';
import { Dealer } from '../../../dealers/dealers.model';
import { ProductService } from '../../../products/product.service';
import { ProductNameList } from '../../../products/product.model';
import { ActivatedRoute, Router } from '@angular/router';
import { SnackbarService } from '../../../services/snackbar.service';
import { ERROR_MESSAGES } from '../../../common/helpers/constants';
import { formatDecimalQuantity } from '../../../utils';
import { DeleteConfirmationModalComponent } from '../../../common/components/delete-confirmation-modal/delete-confirmation-modal.component';

@Component({
  selector: 'app-product-transfer',
  standalone: false,
  templateUrl: './product-transfer.component.html',
  styleUrl: './product-transfer.component.scss',
})
export class ProductTransferComponent implements OnInit, OnDestroy {
  @Input() factoryList: Factory[] = [];
  @ViewChild('transferModal') transferModalRef!: TemplateRef<any>;
  @ViewChild('exportModal') exportModalRef!: TemplateRef<any>;

  protected transfers: ProductTransfer[] = [];
  protected isDataLoading = false;
  protected selectedFilterFactory: string | null = null;
  protected selectedFilterDealer: string | null = null;
  protected selectedFilterWarehouse: string | null = null;
  protected selectedFilterProduct: string | null = null;

  protected totalCount = 0;
  protected hasNextPage = false;
  protected hasPreviousPage = false;
  protected currentPage = 1;

  /** A transfer is one row; its product lines open underneath it. */
  private expandedTransferIds = new Set<string>();

  protected warehouses: RawMaterialWarehouse[] = [];
  protected dealers: Dealer[] = [];
  protected products: ProductNameList[] = [];
  protected factoryStockItems: FactoryStockItem[] = [];
  protected isLoadingFactoryStock = false;
  protected destinationType: 'warehouse' | 'dealer' = 'warehouse';

  /**
   * Batch stock per line, indexed by line. Each line picks its own product, so the
   * batches on offer differ line by line.
   */
  protected lineBatchStocks: FactoryBatchStock[][] = [];
  protected lineBatchLoading: boolean[] = [];

  /**
   * Server messages per line, indexed by line. The 400 body's `items` comes back as long
   * as the array posted, with `{}` for the lines that are fine, so the index is the row.
   */
  protected lineErrors: string[] = [];

  protected transferForm!: FormGroup;
  protected isSaving = false;
  protected formError: string | null = null;
  protected modalRef: NgbModalRef | null = null;

  protected exportFromDate: string = '';
  protected exportToDate: string = '';
  protected isExporting: boolean = false;
  protected exportError: any = null;

  /**
   * Deleting is admin-only server-side (DRF IsAdminUser, which reads `is_staff`). Module
   * enforcement is off and there is no permission code for this action, so a permission
   * grant would still come back 403; `is_staff` is the only signal that matches.
   */
  protected deleteError: string | null = null;
  protected deletingTransferId: string | null = null;

  /**
   * Set when the Production Entries screen sends an admin here to clear the transfers
   * blocking a batch delete. The list API filters on factory but not on batch, so the
   * factory narrows the list server-side and these ids highlight the exact rows.
   */
  protected blockedBatchNumber: string | null = null;
  protected highlightedTransferIds: string[] = [];

  private subscription: Subscription = new Subscription();
  private lineSubscriptions: Subscription[] = [];

  constructor(
    private productionInventoryService: ProductionInventoryService,
    private rawMaterialService: RawMaterialService,
    private dealersService: DealersService,
    private productService: ProductService,
    private modalService: NgbModal,
    private route: ActivatedRoute,
    private router: Router,
    private snackbarService: SnackbarService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.applyIncomingBatchContext();
    this.loadWarehouses();
    this.loadDealers();
    this.loadProducts();
    this.loadTransfers();
  }

  /** Reads the deep link the Production Entries delete error builds. */
  private applyIncomingBatchContext(): void {
    const params = this.route.snapshot.queryParams;

    this.blockedBatchNumber = params['batch'] || null;
    this.highlightedTransferIds = (params['transfers'] || '').split(',').filter((id: string) => !!id);

    if (params['factory']) {
      this.selectedFilterFactory = params['factory'];
    }
  }

  protected isHighlighted(transfer: ProductTransfer): boolean {
    return this.highlightedTransferIds.includes(transfer.id);
  }

  /** How many of the blocking transfers are visible on the page currently loaded. */
  protected get highlightedOnThisPage(): number {
    return this.transfers.filter(t => this.isHighlighted(t)).length;
  }

  protected clearBatchContext(): void {
    this.blockedBatchNumber = null;
    this.highlightedTransferIds = [];

    // Drop the deep-link params too, so switching tabs and coming back does not
    // resurrect a batch the admin has finished with.
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { batch: null, transfers: null },
      queryParamsHandling: 'merge',
    });
  }

  protected isExpanded(transfer: ProductTransfer): boolean {
    return this.expandedTransferIds.has(transfer.id);
  }

  protected toggleExpanded(transfer: ProductTransfer): void {
    if (!this.expandedTransferIds.delete(transfer.id)) {
      this.expandedTransferIds.add(transfer.id);
    }
  }

  private initForm(): void {
    this.transferForm = this.fb.group({
      factory: [null, Validators.required],
      warehouse: [null, Validators.required],
      dealer: [null],
      transfer_date: ['', Validators.required],
      invoice_number: [null],
      invoice_date: [null],
      vehicle_number: [null],
      driver_name: [null],
      items: this.fb.array([]),
    });

    // A factory change invalidates every line: the stock on offer is that factory's.
    this.transferForm.get('factory')?.valueChanges.subscribe(factoryId => {
      this.factoryStockItems = [];
      this.items.controls.forEach((line, index) => {
        line.patchValue({ product_packaging: null, production_entry: null }, { emitEvent: false });
        this.lineBatchStocks[index] = [];
      });
      if (factoryId) {
        this.loadFactoryStock(factoryId);
      }
    });

    this.resetItems();
  }

  protected get items(): FormArray {
    return this.transferForm.get('items') as FormArray;
  }

  private createItemGroup(): FormGroup {
    return this.fb.group({
      product_packaging: [null, Validators.required],
      production_entry: [null, Validators.required],
      // Boxes, integer, at least 1. A fraction is a 400, so the pattern blocks it here.
      box_quantity: [null, [Validators.required, Validators.min(1), Validators.pattern(/^\d+$/)]],
    });
  }

  /** Drops every line and starts again on one blank one. */
  private resetItems(): void {
    this.lineSubscriptions.forEach(s => s.unsubscribe());
    this.lineSubscriptions = [];
    this.lineBatchStocks = [];
    this.lineBatchLoading = [];
    this.lineErrors = [];
    this.items.clear();
    this.addItem();
  }

  protected addItem(): void {
    const index = this.items.length;
    this.items.push(this.createItemGroup());
    this.lineBatchStocks[index] = [];
    this.lineBatchLoading[index] = false;
    this.lineErrors[index] = '';
    this.watchLine(index);
  }

  protected removeItem(index: number): void {
    this.items.removeAt(index);
    this.lineBatchStocks.splice(index, 1);
    this.lineBatchLoading.splice(index, 1);
    this.lineErrors.splice(index, 1);
    this.lineSubscriptions[index]?.unsubscribe();
    this.lineSubscriptions.splice(index, 1);
  }

  private watchLine(index: number): void {
    const line = this.items.at(index);
    const subscription = new Subscription();

    subscription.add(
      line.get('product_packaging')!.valueChanges.subscribe(packagingId => {
        // The batch belongs to the packaging on the same line, so a new product drops it.
        line.patchValue({ production_entry: null }, { emitEvent: false });
        this.lineBatchStocks[index] = [];
        const factoryId = this.transferForm.get('factory')?.value;
        if (factoryId && packagingId) {
          this.loadBatchStocks(factoryId, packagingId, index);
        }
      })
    );

    // A rejected line stops being rejected once it is edited.
    subscription.add(line.valueChanges.subscribe(() => (this.lineErrors[index] = '')));

    this.lineSubscriptions[index] = subscription;
  }

  protected onDestinationTypeChange(): void {
    const warehouseCtrl = this.transferForm.get('warehouse')!;
    const dealerCtrl = this.transferForm.get('dealer')!;
    if (this.destinationType === 'warehouse') {
      warehouseCtrl.setValidators(Validators.required);
      dealerCtrl.clearValidators();
      dealerCtrl.setValue(null);
    } else {
      dealerCtrl.setValidators(Validators.required);
      warehouseCtrl.clearValidators();
      warehouseCtrl.setValue(null);
    }
    warehouseCtrl.updateValueAndValidity();
    dealerCtrl.updateValueAndValidity();
  }

  private loadWarehouses(): void {
    this.rawMaterialService.get_warehouses().subscribe({
      next: (warehouses: RawMaterialWarehouse[]) => {
        this.warehouses = warehouses;
      },
    });
  }

  private loadDealers(): void {
    this.dealersService.lighter_list().subscribe({
      next: (dealers: Dealer[]) => {
        this.dealers = dealers;
      },
    });
  }

  private loadProducts(): void {
    this.productService.lighter_list().subscribe({
      next: (products: ProductNameList[]) => {
        this.products = products;
      },
    });
  }

  private loadFactoryStock(factoryId: string): void {
    this.isLoadingFactoryStock = true;
    this.productionInventoryService
      .getFactoryStocks({ factory: factoryId, page_size: 100 })
      .pipe(finalize(() => (this.isLoadingFactoryStock = false)))
      .subscribe({
        next: (response: PaginatedResponse<FactoryStockItem>) => {
          this.factoryStockItems = response.results || [];
        },
      });
  }

  private loadBatchStocks(factoryId: string, packagingId: string, index: number): void {
    this.lineBatchLoading[index] = true;
    this.productionInventoryService
      .getFactoryBatchStocks({ factory: factoryId, product_packaging: packagingId, page_size: 100 })
      .pipe(finalize(() => (this.lineBatchLoading[index] = false)))
      .subscribe({
        next: (response: PaginatedResponse<FactoryBatchStock>) => {
          this.lineBatchStocks[index] = response.results || [];
        },
      });
  }

  private loadTransfers(): void {
    this.isDataLoading = true;

    const params: any = { page: this.currentPage };
    if (this.selectedFilterFactory) params['factory'] = this.selectedFilterFactory;
    if (this.selectedFilterDealer) params['dealer'] = this.selectedFilterDealer;
    if (this.selectedFilterWarehouse) params['warehouse'] = this.selectedFilterWarehouse;
    if (this.selectedFilterProduct) params['product'] = this.selectedFilterProduct;

    this.subscription.add(
      this.productionInventoryService
        .getProductTransfers(params)
        .pipe(finalize(() => (this.isDataLoading = false)))
        .subscribe({
          next: (response: PaginatedResponse<ProductTransfer>) => {
            this.transfers = response.results || [];
            this.totalCount = response.count;
            this.hasNextPage = !!response.next;
            this.hasPreviousPage = !!response.previous;

            // The admin came here to find a batch, and it is on a line, not the row.
            this.transfers.filter(t => this.isHighlighted(t)).forEach(t => this.expandedTransferIds.add(t.id));
          },
        })
    );
  }

  protected onFilterFactoryChange(): void {
    this.currentPage = 1;
    this.loadTransfers();
  }

  protected onFilterDealerChange(): void {
    this.currentPage = 1;
    this.loadTransfers();
  }

  protected onFilterWarehouseChange(): void {
    this.currentPage = 1;
    this.loadTransfers();
  }

  protected onFilterProductChange(): void {
    this.currentPage = 1;
    this.loadTransfers();
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this.loadTransfers();
  }

  /**
   * Transfers cannot be edited, so a wrong one is deleted and re-entered in full. There is
   * no way to remove a single line: the delete reverses every line at once, which the
   * dialog says in products and boxes.
   */
  protected onDeleteTransfer(transfer: ProductTransfer): void {
    const ref = this.modalService.open(DeleteConfirmationModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    ref.componentInstance.title = 'Delete this transfer?';
    ref.componentInstance.message = this.buildDeleteMessage(transfer);
    ref.componentInstance.warning = 'This cannot be undone.';

    ref.result.then(
      () => this.deleteTransfer(transfer),
      () => {} // dismissed
    );
  }

  /**
   * The stock lands somewhere different by destination: a warehouse transfer comes off
   * that warehouse, a dealer one only returns the units to the factory.
   */
  private buildDeleteMessage(transfer: ProductTransfer): string {
    const units = formatDecimalQuantity(transfer.total_quantity, '');
    const moved =
      `${this.productCount(transfer.item_count)} (${this.boxCount(transfer.total_box_quantity)}` +
      `${units ? `, ${units} units` : ''}) on this transfer are reversed together`;

    if (transfer.warehouse_name) {
      return `${moved}: they come off ${transfer.warehouse_name} and go back to ${transfer.factory_name}.`;
    }

    return `${moved}: they go back to ${transfer.factory_name}. The dealer record of this dispatch is removed.`;
  }

  protected productCount(count: number): string {
    return `${count} ${count === 1 ? 'product' : 'products'}`;
  }

  protected boxCount(count: number): string {
    return `${count} ${count === 1 ? 'box' : 'boxes'}`;
  }

  private deleteTransfer(transfer: ProductTransfer): void {
    this.deleteError = null;
    this.deletingTransferId = transfer.id;

    this.productionInventoryService
      .deleteProductTransfer(transfer.id)
      .pipe(finalize(() => (this.deletingTransferId = null)))
      .subscribe({
        next: () => {
          this.snackbarService.show(
            `Transfer of ${this.productCount(transfer.item_count)} to ${transfer.destination_name} deleted. ` +
              `The units are back at ${transfer.factory_name}.`
          );

          this.expandedTransferIds.delete(transfer.id);
          this.highlightedTransferIds = this.highlightedTransferIds.filter(id => id !== transfer.id);
          if (this.blockedBatchNumber && this.highlightedTransferIds.length === 0) {
            this.blockedBatchNumber = null;
          }

          // Deleting the only row on a later page would otherwise land on a blank page.
          if (this.transfers.length === 1 && this.currentPage > 1) {
            this.currentPage -= 1;
          }

          this.loadTransfers();
        },
        error: (error: any) => {
          this.deleteError = this.parseDeleteError(error);
        },
      });
  }

  /**
   * Unlike a production entry delete, a refused transfer delete is unrecoverable from the
   * UI: the stock has left that warehouse and the transfer record has to stand. The
   * server's `detail` says so, so it is shown verbatim rather than a generic failure line.
   * Nothing was reversed when this comes back, not even the lines that would have fitted.
   */
  private parseDeleteError(error: any): string {
    const body: Partial<ProductTransferDeleteError> = error?.error ?? {};
    const detail = typeof body.detail === 'string' ? body.detail.trim() : '';

    if (error?.status === 400 && detail) return detail;
    if (error?.status === 403) return detail || ERROR_MESSAGES['403'];
    if (error?.status === 500) return ERROR_MESSAGES['500'];

    return detail || ERROR_MESSAGES.DEFAULT;
  }

  protected openTransferModal(): void {
    this.formError = null;
    this.factoryStockItems = [];
    this.destinationType = 'warehouse';
    this.transferForm.reset();
    this.resetItems();
    // Reset validators to default (warehouse required)
    this.transferForm.get('warehouse')!.setValidators(Validators.required);
    this.transferForm.get('dealer')!.clearValidators();
    this.transferForm.get('warehouse')!.updateValueAndValidity();
    this.transferForm.get('dealer')!.updateValueAndValidity();

    const options: NgbModalOptions = { backdrop: 'static', size: 'xl' };
    this.modalRef = this.modalService.open(this.transferModalRef, options);
  }

  protected onSubmitTransfer(): void {
    if (this.transferForm.invalid || this.hasDuplicateBatchLine) return;

    this.isSaving = true;
    this.formError = null;
    this.lineErrors = this.items.controls.map(() => '');

    const body: ProductTransferRequest = {
      factory: this.transferForm.get('factory')!.value,
      transfer_date: this.transferForm.get('transfer_date')!.value,
      invoice_number: this.transferForm.get('invoice_number')!.value || null,
      invoice_date: this.transferForm.get('invoice_date')!.value || null,
      vehicle_number: this.transferForm.get('vehicle_number')!.value || null,
      driver_name: this.transferForm.get('driver_name')!.value || null,
      items: this.items.controls.map(line => ({
        product_packaging: line.get('product_packaging')!.value,
        production_entry: line.get('production_entry')!.value,
        box_quantity: Number(line.get('box_quantity')!.value),
      })),
    };
    if (this.destinationType === 'warehouse') {
      body.warehouse = this.transferForm.get('warehouse')!.value;
    } else {
      body.dealer = this.transferForm.get('dealer')!.value;
    }

    this.productionInventoryService
      .postProductTransfer(body)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: (transfer: ProductTransfer) => {
          this.transfers = [transfer, ...this.transfers];
          this.totalCount += 1;
          this.modalRef?.dismiss();
        },
        error: (error: any) => this.applyCreateError(error),
      });
  }

  /**
   * A short line rejects the whole transfer: nothing is created and no stock moves. So
   * every offending row is marked at once and the rest of the input stays put, rather than
   * the form retrying line by line.
   */
  private applyCreateError(error: any): void {
    const body = error?.error ?? {};
    const lines: ProductTransferItemError[] = Array.isArray(body.items) ? body.items : [];

    this.lineErrors = this.items.controls.map((_, index) => this.lineErrorMessage(lines[index]));

    const rejected = this.lineErrors.filter(message => !!message).length;
    const header = this.headerErrorMessage(body);

    if (header) {
      this.formError = header;
      return;
    }

    this.formError = rejected
      ? `${rejected} of ${this.items.length} ${rejected === 1 ? 'line was' : 'lines were'} rejected, so nothing ` +
        `was transferred and no stock moved. Fix the lines marked below and submit again.`
      : 'Failed to create transfer. Please try again.';
  }

  /** Everything the server said about one line, in one sentence for that row. */
  private lineErrorMessage(line: ProductTransferItemError | null | undefined): string {
    if (!line) return '';

    return [line.box_quantity, line.production_entry, line.product_packaging]
      .map(value => this.firstMessage(value))
      .filter(message => !!message)
      .join(' ');
  }

  /** Header-level problems: no destination, both destinations, a bad date. */
  private headerErrorMessage(body: any): string {
    if (typeof body === 'string') return body.trim();

    const detail = this.firstMessage(body?.detail) || this.firstMessage(body?.non_field_errors);
    if (detail) return detail;

    for (const [field, value] of Object.entries(body ?? {})) {
      if (field === 'items') continue;
      const message = this.firstMessage(value);
      if (message) return message;
    }

    return '';
  }

  /** DRF answers with either a string or a list of them, depending on the check. */
  private firstMessage(value: any): string {
    if (typeof value === 'string') return value.trim();
    if (Array.isArray(value)) return value.map(entry => this.firstMessage(entry)).find(entry => !!entry) ?? '';

    return '';
  }

  protected isInvalid(field: string): boolean {
    const ctrl = this.transferForm.get(field);
    return (ctrl?.invalid && (ctrl.touched || ctrl.dirty)) ?? false;
  }

  protected isLineInvalid(index: number, field: string): boolean {
    const ctrl = this.items.at(index).get(field);
    return (ctrl?.invalid && (ctrl.touched || ctrl.dirty)) ?? false;
  }

  protected selectedBatchFor(index: number): FactoryBatchStock | null {
    const entryId = this.items.at(index).get('production_entry')?.value;
    return this.lineBatchStocks[index]?.find(b => b.production_entry === entryId) ?? null;
  }

  /**
   * The same batch twice is a 400 that names the earlier line, so the form catches it
   * first and points at the later row. The fix is one row carrying the whole quantity.
   */
  protected duplicateOfLine(index: number): number | null {
    const entryId = this.items.at(index).get('production_entry')?.value;
    if (!entryId) return null;

    for (let earlier = 0; earlier < index; earlier++) {
      if (this.items.at(earlier).get('production_entry')?.value === entryId) return earlier + 1;
    }

    return null;
  }

  protected get hasDuplicateBatchLine(): boolean {
    return this.items.controls.some((_, index) => this.duplicateOfLine(index) !== null);
  }

  /** Boxes keyed in against boxes the batch holds. A warning only: the server decides. */
  protected boxesOverAvailable(index: number): number | null {
    const batch = this.selectedBatchFor(index);
    const requested = Number(this.items.at(index).get('box_quantity')?.value);
    if (!batch || !Number.isFinite(requested) || requested <= 0) return null;

    const available = Number(batch.boxes_available);
    return requested > available ? available : null;
  }

  protected get totalBoxesOnForm(): number {
    return this.items.controls.reduce((total, line) => {
      const boxes = Number(line.get('box_quantity')?.value);
      return total + (Number.isFinite(boxes) ? boxes : 0);
    }, 0);
  }

  protected openExportModal(): void {
    this.exportFromDate = '';
    this.exportToDate = '';
    this.exportError = null;
    this.modalService.open(this.exportModalRef, { size: 'md' });
  }

  protected onExportSubmit(modalRef: any): void {
    this.isExporting = true;
    this.exportError = null;

    const q: Record<string, any> = {};
    if (this.exportFromDate) q['from_date'] = this.exportFromDate;
    if (this.exportToDate) q['to_date'] = this.exportToDate;

    this.productionInventoryService
      .export_product_transfers(q)
      .pipe(finalize(() => (this.isExporting = false)))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'product_transfers.xlsx';
          a.click();
          URL.revokeObjectURL(url);
          modalRef.close();
        },
        error: err => {
          this.exportError = err;
        },
      });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.lineSubscriptions.forEach(s => s.unsubscribe());
    this.modalRef?.dismiss();
  }
}
